import React from "react";
import Header from "../components/Header";

export default function CompletionSystemPage() {
  return (
    <>
    <Header />
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 text-[#003366] font-sans p-6">
      {/* Header with improved styling */}
      <div className="flex items-center gap-6 mb-8">
        <div className="relative">
          <div className="w-12 h-20 bg-gradient-to-b from-blue-500 to-blue-800 rounded-lg shadow-lg"></div>
          <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-blue-400 rounded-full"></div>
        </div>
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-900 to-blue-600 bg-clip-text text-transparent">
            6-Completion System
          </h1>
          <p className="text-sm text-blue-700 mt-1">End-to-End Project Completion Management</p>
        </div>
      </div>

      {/* System Overview Box - Enhanced */}
      <div className="rounded-2xl shadow-xl overflow-hidden mb-8 bg-gradient-to-r from-blue-900 to-blue-700 text-white transform transition-transform hover:scale-[1.01]">
        <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-center py-4 text-xl font-bold border-b border-blue-400">
          <div className="flex items-center justify-center gap-3">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
            System Overview
          </div>
        </div>
        <div className="p-6">
          <p className="text-base leading-relaxed">
            From work package issuance to progress tracking, the system checks supplier execution,
            generates an issue list based on inspection results, and manages issue list remediation
            until all ITRs are completed. Once completed, the issue list is fully processed, supporting
            materials like red-line drawings and STQ are complete, and the FAC certificate is signed.
          </p>
          <div className="flex gap-4 mt-4">
            <span className="px-3 py-1 bg-blue-600 rounded-full text-xs font-medium">Work Package Issuance</span>
            <span className="px-3 py-1 bg-blue-600 rounded-full text-xs font-medium">Progress Tracking</span>
            <span className="px-3 py-1 bg-blue-600 rounded-full text-xs font-medium">FAC Certification</span>
          </div>
        </div>
      </div>

      {/* Features Section - Enhanced */}
      <div className="rounded-2xl shadow-xl overflow-hidden mb-8 bg-white border border-gray-200">
        <div className="text-center py-4 text-2xl font-bold bg-gradient-to-r from-red-50 to-white">
          <h2 className="text-[#B03030]">Key Features</h2>
          <div className="w-24 h-1 bg-[#B03030] mx-auto mt-2 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 text-white text-sm font-medium">
          <div className="bg-gradient-to-br from-orange-500 to-red-500 p-6 border-r border-white/30 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
              </svg>
            </div>
            <h3 className="font-bold text-lg mb-3">Visualized Display</h3>
            <p className="text-sm leading-relaxed opacity-90">
              Consolidate and display MCC certificate data across time dimensions, using block
              elements and icons to show certificate status and Tag ITR completion with intuitive dashboards.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-red-600 to-red-700 p-6 border-r border-white/30 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="font-bold text-lg mb-3">On-site Management Alignment</h3>
            <p className="text-sm leading-relaxed opacity-90">
              Deeply aligned with actual on-site business management requirements and operational logic,
              providing real-time visibility and control over project execution.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-red-800 to-red-900 p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="font-bold text-lg mb-3">Document Management</h3>
            <p className="text-sm leading-relaxed opacity-90">
              Document identification codes enable batch rapid archiving, with automatic categorization
              after coding and batch import, significantly reducing documentation management pressure.
            </p>
          </div>
        </div>
      </div>

      {/* Image Gallery with improved layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-2xl shadow-2xl overflow-hidden bg-white transform transition-transform hover:scale-[1.02]">
          <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white py-3 px-6 font-semibold">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
              </svg>
              System Architecture Diagram
            </div>
          </div>
          <div className="p-4">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
              alt="Completion System Architecture Diagram"
              className="w-full h-64 object-cover rounded-lg"
            />
            <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded-lg">
              Visual representation of the 6-Completion System workflow and component integration
            </p>
          </div>
        </div>

        <div className="rounded-2xl shadow-2xl overflow-hidden bg-white transform transition-transform hover:scale-[1.02]">
          <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white py-3 px-6 font-semibold">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              System Dashboard Preview
            </div>
          </div>
          <div className="p-4">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
              alt="System Dashboard Interface"
              className="w-full h-64 object-cover rounded-lg"
            />
            <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded-lg">
              Real-time dashboard showing project completion status, milestones, and key metrics
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-md border border-gray-100">
          <div className="text-2xl font-bold text-blue-800">99.8%</div>
          <div className="text-sm text-gray-600">Completion Accuracy</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-md border border-gray-100">
          <div className="text-2xl font-bold text-blue-800">40%</div>
          <div className="text-sm text-gray-600">Time Reduction</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-md border border-gray-100">
          <div className="text-2xl font-bold text-blue-800">1000+</div>
          <div className="text-sm text-gray-600">Projects Managed</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-md border border-gray-100">
          <div className="text-2xl font-bold text-blue-800">24/7</div>
          <div className="text-sm text-gray-600">System Availability</div>
        </div>
      </div>
    </div>
    </>
  );
}